import React from 'react'

import { Table, TableCol, TableHead, TableRow } from '../../../../../components/pdf/Table'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { HEADING_COLOR } from '../_helpers/brand'
import { LANDSCAPE_PAGE_STYLE, LandscapePageDefinition } from './_shared/Landscape'

// Cor de borda desta tabela: preta — diferente das demais tabelas do PGR Bradesco (que
// usam borda vermelha via `_shared/BradescoTable.js`). Confirmado por inspeção pixel-a-
// pixel do modelo real (páginas 52-54): a grade do Plano de Ação usa borda preta e
// cabeçalho cinza-claro (não vermelho) para "Nome da ação/Ação/Periodicidade/Cronograma"
// e para os rótulos de ano ("2026"/"2027"/"2028"); só as células de mês dentro do período
// agendado usam preenchimento vermelho sólido.
const BORDER_COLOR = '#000000'
const HEADER_BG = '#E8E8E8'

const ANOS = ['2026', '2027', '2028']
const MESES_DO_ANO = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ']

// Índice (0-based, 0=Jan/26 a 35=Dez/28) do primeiro e do último mês do período
// destacado em vermelho: contínuo de Maio/26 a Maio/28 (25 meses) — confirmado por
// amostragem de pixel nas 3 tabelas do modelo real (páginas 52-54): as 3 ações têm
// exatamente o mesmo período destacado, não períodos diferentes por ação como uma
// revisão anterior deste arquivo presumia (ver histórico no PR).
const INDICE_INICIO_DESTAQUE = 4 // Mai/26
const INDICE_FIM_DESTAQUE = 28 // Mai/28
const destaque = indiceMes => indiceMes >= INDICE_INICIO_DESTAQUE && indiceMes <= INDICE_FIM_DESTAQUE

/**
 * Seção 19 - Plano de Ação. Conteúdo real (não placeholder) confirmado por Julia Buzzo:
 * as 3 ações e suas periodicidades são fixas, replicadas por completo em toda dependência
 * (não há indicação de conteúdo dinâmico por atividade).
 *
 * Estrutura corrigida por inspeção pixel-a-pixel do modelo real (páginas 52-54) — a versão
 * anterior tinha uma única tabela com cabeçalho de mês vermelho e as 3 ações como linhas.
 * O modelo real tem, em vez disso, UMA TABELA POR AÇÃO (cabeçalho "Nome da ação | Ação |
 * Periodicidade | Cronograma" repetido a cada ação), com borda preta e cabeçalho cinza; sob
 * "Cronograma", cada ano (2026/2027/2028) tem sua própria linha de rótulo (cinza, colSpan
 * 12) seguida da linha dos 12 meses daquele ano — só a célula do mês agendado fica com
 * preenchimento vermelho sólido e texto branco; os demais meses ficam em branco com texto
 * preto. O período agendado (Mai/26 a Mai/28, contínuo) é o mesmo nas 3 tabelas.
 *
 * Orientação paisagem: confirmado no PDF-modelo (páginas 52-54) — ver `_shared/Landscape.js`
 * para o mecanismo (CSS Paged Media, sem tocar em `components/pdf/`) e a limitação de
 * validação visual.
 *
 * Cabeçalho repetido em quebra de página ("Nome da ação | Ação | Periodicidade |
 * Cronograma", envolvido em `<thead style={{display:'table-header-group'}}>` abaixo): o
 * modelo real não repete cabeçalho de tabela em nenhuma página (o autor reinsere
 * manualmente a cada bloco), mas `pgr_2026_input_contract.json`
 * (`options.repeatRiskInventoryHeaderOnPageBreak`) e `PGR_2026_ESPECIFICACAO_TECNICA.md`
 * (seção H, `repeatTableHeader`) recomendam habilitar a repetição especificamente nesta
 * tabela ("T18") e na do Inventário de Riscos ("T17", ver `18.InventarioRiscosSection.js`)
 * — nenhuma outra tabela do documento. Mesma limitação de validação visual das demais
 * regras de CSS Paged Media deste módulo (sem navegador para gerar o PDF real aqui).
 */
const ACOES = [
  {
    nome: 'Monitoramento dos fatores psicossociais relacionados ao trabalho',
    acao: 'Manter os comitês multidisciplinares periódicos de ergonomia seguindo os parâmetros e ações previstas e validadas em comitê',
    periodicidade: 'Mensal'
  },
  {
    nome: 'Aprofundamento dos fatores psicossociais relacionados ao trabalho',
    acao: 'Aprofundar o estudo a partir dos dados sentinela conforme descrito no Gerenciamento dos Riscos Ocupacionais – GRO',
    periodicidade: 'Sob análise da demanda estudada em 1º e 2º nível'
  },
  {
    nome: 'Revisão do PGR',
    acao: 'Verificar a execução das ações planejadas e revisar os riscos para elaboração da revisão do PGR Riscos Ocupacionais – GRO',
    periodicidade: 'Bienal'
  }
]

const AcaoTable = ({ acao }) => (
  <Table color={BORDER_COLOR} width="100%" fixedLayout>
    <thead style={{ display: 'table-header-group' }}>
      <TableRow color={BORDER_COLOR}>
        <TableHead color={BORDER_COLOR} bgColor={HEADER_BG} width="12%">
          <Text bold align="center" size="xsmall">
            Nome da ação
          </Text>
        </TableHead>
        <TableHead color={BORDER_COLOR} bgColor={HEADER_BG} width="18%">
          <Text bold align="center" size="xsmall">
            Ação
          </Text>
        </TableHead>
        <TableHead color={BORDER_COLOR} bgColor={HEADER_BG} width="10%">
          <Text bold align="center" size="xsmall">
            Periodicidade
          </Text>
        </TableHead>
        <TableHead color={BORDER_COLOR} bgColor={HEADER_BG} colSpan={12}>
          <Text bold align="center" size="xsmall">
            Cronograma
          </Text>
        </TableHead>
      </TableRow>
    </thead>

    {ANOS.map((ano, anoIndex) => (
      <React.Fragment key={ano}>
        <TableRow color={BORDER_COLOR}>
          {anoIndex === 0 && (
            <>
              <TableCol color={BORDER_COLOR} rowSpan={6} style={{ verticalAlign: 'middle' }}>
                <Text size="xsmall">{acao.nome}</Text>
              </TableCol>
              <TableCol color={BORDER_COLOR} rowSpan={6} style={{ verticalAlign: 'middle' }}>
                <Text size="xsmall">{acao.acao}</Text>
              </TableCol>
              <TableCol color={BORDER_COLOR} rowSpan={6} style={{ verticalAlign: 'middle' }}>
                <Text align="center" size="xsmall">
                  {acao.periodicidade}
                </Text>
              </TableCol>
            </>
          )}
          <TableHead color={BORDER_COLOR} bgColor={HEADER_BG} colSpan={12}>
            <Text bold align="center" size="xsmall">
              {ano}
            </Text>
          </TableHead>
        </TableRow>
        <TableRow color={BORDER_COLOR}>
          {MESES_DO_ANO.map((mes, mesIndex) => {
            const indiceMes = anoIndex * 12 + mesIndex
            const agendado = destaque(indiceMes)
            return (
              <TableCol key={mes} color={BORDER_COLOR} bgColor={agendado ? '#FF0000' : undefined}>
                <Text bold align="center" size="xxsmall" color={agendado ? '#FFFFFF' : '#000000'}>
                  {mes}
                </Text>
              </TableCol>
            )
          })}
        </TableRow>
      </React.Fragment>
    ))}
  </Table>
)

const PlanoDeAcaoSection = () => (
  <View pageBreakAfter id="19" style={LANDSCAPE_PAGE_STYLE}>
    <LandscapePageDefinition />
    <Separator weight={15} />
    <Text bold size="small" color={HEADING_COLOR}>
      19. PLANO DE AÇÃO
    </Text>
    <Separator weight={8} />

    {ACOES.map(acao => (
      <View key={acao.nome} style={{ overflowX: 'auto' }}>
        <AcaoTable acao={acao} />
        <Separator weight={15} />
      </View>
    ))}
  </View>
)

export default PlanoDeAcaoSection
